import { Fragment, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';

import { useI18n } from '../../hooks/useI18n';
import { TRegionInstance } from '../../types';

import { FlagIcon } from '../Flags/flagIcons';
import { Text } from '../TextLocalized';
import { translations } from '../../translations';

interface IListOptionItemProps {
  /**
   * The item details, with the label and icon/flag to render
   */
  item: TRegionInstance;
}

/**
 * -----------------------------------------------------------------------------
 * This renders the the flag and text content of the item
 *
 * @param {{ item }}
 * @returns
 */
function ItemDetailsPreview({ item }: IListOptionItemProps) {
  return (
    <>
      <FlagIcon id={item.icon} className="shrink-0 rounded-md" size={22} />
      <span
        className="
          ui-active:text-redx)]
          truncate text-xs text-gray-600 sm:text-sm
        "
      >
        <Text text={item.label} />
      </span>
    </>
  );
}

/**
 * -----------------------------------------------------------------------------
 * This renders each individual item from the dropdown options list.
 * In this case, a country flag and its name with a checkmark to show selection.
 *
 *
 * @param TRegionInstance
 * @returns
 */
function ListOptionItem({ item }: IListOptionItemProps) {
  return (
    <Listbox.Option
      className="
        ui-active:bg-red-50 ui-active:text-vioredcted::text-red-700 relative
        flex cursor-default select-none py-2 text-xs text-gray-700 sm:text-sm
      "
      value={item}
    >
      <span
        className="
          ui-selected:max-w-[calc(100%-64px)] mx-3 flex max-w-[calc(100%-22px)]
          grow items-center
        "
      >
        {item ? (
          <ItemDetailsPreview item={item} />
        ) : (
          <span className="font-light text-gray-500">
            <Text text={translations.selectARegion} />
          </span>
        )}
      </span>
      <CheckIcon
        className="
            ui-selected:flex ui-active:text-red-800
             mx-2.5 hidden
            h-5 w-5 shrink-0 items-center text-red-600
          "
        aria-hidden="true"
      />
    </Listbox.Option>
  );
}

interface IRegionSelectDropdownFieldProps {
  /**
   * This renders the default selected instance passed to this dropdown.
   */
  initialRegion: TRegionInstance | null;
  /**
   * Callback supplied to the parent component to provide the selected country.
   */
  onSelectRegion: (selectedRegion: TRegionInstance) => void;
}

/**
 * -----------------------------------------------------------------------------
 * Dropdown provides UX for choosing a country/region from a list of supported
 * countries/regions/territories, rendering the country flag and name, alongside
 * a checkmark to show selected.
 *
 * TODO: 2. Get the Options from the Backend and Cache them during app session.
 * TODO: 3. Add Tests and Stories to the component
 *
 * @returns JSX.Element
 */
export function RegionSelectDropdownField({
  onSelectRegion,
  initialRegion,
}: IRegionSelectDropdownFieldProps) {
  const { regionsConfig } = useI18n();

  const [selected, setSelected] = useState<TRegionInstance | null>(
    initialRegion
  );

  const handleSelectionChange = (value: TRegionInstance) => {
    onSelectRegion(value);
    setSelected(value);
  };

  return (
    <Listbox value={selected} onChange={handleSelectionChange}>
      {({ open }) => (
        <div className="ui-open:hidden">
          <Listbox.Label className="form-label">
            <Text text={translations.region} />
          </Listbox.Label>
          <div className="relative">
            <Listbox.Button className="form-input-field flex">
              <span className="flex max-w-[calc(100%-22px)] grow items-center">
                {selected ? (
                  <ItemDetailsPreview item={selected} />
                ) : (
                  <span className="font-light text-gray-500">
                    <Text text={translations.selectARegion} />
                  </span>
                )}
              </span>
              <ChevronDownIcon
                className="pointer-events-none h-5 w-5 shrink-0 text-gray-400"
                aria-hidden="true"
              />
            </Listbox.Button>

            <Transition
              as={Fragment}
              show={open}
              enter="transition ease-in duration-150"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                className="
                  absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md
                  bg-white py-1 text-base shadow-lg ring-1 ring-black
                  ring-opacity-5 focus:outline-none sm:text-sm
                "
              >
                {regionsConfig.map((region) => (
                  <ListOptionItem key={region.id} item={region} />
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </div>
      )}
    </Listbox>
  );
}
